import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Toaster } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

// Components
import Layout from './components/layout/Layout';
import Dashboard from './pages/Dashboard';
import Clients from './pages/Clients';
import AddClient from './pages/AddClient';
import Projects from './pages/Projects';
import AddProject from './pages/AddProject';
import Associates from './pages/Associates';
import AddAssociate from './pages/AddAssociate';
import Designations from './pages/Designations';
import AddDesignation from './pages/AddDesignation';

import FinanceTabs from './pages/FinanceTabs';
import Invoices from './pages/Invoices';
import AddInvoice from './pages/AddInvoice';
import AddPayroll from './pages/AddPayroll';
import Timesheet from './pages/Timesheet';
import TimesheetList from './pages/TimesheetList';
import ManagerTimesheetApproval from './pages/ManagerTimesheetApproval';
import Users from './pages/Users';
import EditUser from './pages/EditUser';
import OnboardUser from './pages/OnboardUser';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import SetPassword from './pages/SetPassword';
import NotFound from './pages/NotFound';
import Profile from './pages/Profile';

// Context
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Styles
// App.css removed - using Tailwind CSS instead

// Create a custom theme based on the HR system design (primary adjusted to #613F75)
const theme = extendTheme({
  colors: {
    primary: {
      50: '#f3f1ff',
      100: '#e5dbff',
      200: '#d1bfff',
      300: '#b794ff',
      400: '#9f66ff',
      500: '#7B4BC1',
      600: '#6A478E',
      700: '#613F75', // Sidebar background
      800: '#53345F',
      900: '#3F2746',
    },
    status: {
      present: '#10b981', // Green
      absent: '#ef4444',  // Red
      late: '#f59e0b',    // Yellow
      halfDay: '#f97316', // Orange
      onLeave: '#3b82f6', // Blue
      pending: '#6b7280', // Gray
    },
    accent: {
      blue: '#3b82f6',
      yellow: '#f59e0b',
    },
    background: {
      page: '#f8fafc',
      card: '#ffffff',
    }
  },
  fonts: {
    heading: 'Inter, sans-serif',
    body: 'Inter, sans-serif',
  },
  components: {
    Button: {
      defaultProps: {
        colorScheme: 'primary',
      },
    },
  },
});

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  let userRole: string | undefined = undefined;
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const decoded: any = jwtDecode(token as string);
      userRole = decoded.role;
    } catch (err) {
      userRole = undefined;
    }
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ChakraProvider theme={theme}>
        <Router>
          <AuthProvider>
            <div className="App">
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/set-password" element={<SetPassword />} />
                <Route path="/" element={<Layout />}>
                  <Route index element={<Dashboard />} />
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route
                    path="clients"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <Clients />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="clients/add"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <AddClient />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="clients/edit/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <AddClient />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="projects"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <Projects />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="projects/add"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <AddProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="projects/edit/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin", "hr"]}
                        userRole={userRole}
                      >
                        <AddProject />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance"
                    element={
                      <ProtectedRoute
                        allowedRoles={["hr", "admin"]}
                        userRole={userRole}
                      >
                        <FinanceTabs />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="invoices"
                    element={
                      <ProtectedRoute allowedRoles={["hr"]} userRole={userRole}>
                        <Invoices />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="add-invoice"
                    element={
                      <ProtectedRoute allowedRoles={["hr"]} userRole={userRole}>
                        <AddInvoice />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="add-payroll"
                    element={
                      <ProtectedRoute allowedRoles={["hr"]} userRole={userRole}>
                        <AddPayroll />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="timesheet"
                    element={
                      <ProtectedRoute
                        allowedRoles={["user"]}
                        userRole={userRole}
                      >
                        <TimesheetList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="submit-timesheet"
                    element={
                      <ProtectedRoute
                        allowedRoles={["user"]}
                        userRole={userRole}
                      >
                        <Timesheet />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="manager-timesheet-approval"
                    element={
                      <ProtectedRoute
                        allowedRoles={["manager"]}
                        userRole={userRole}
                      >
                        <ManagerTimesheetApproval />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="associates"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <Associates />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="associates/add"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <AddAssociate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="associates/edit/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <AddAssociate />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="designations"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <Designations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="designations/add"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <AddDesignation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="designations/edit/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <AddDesignation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    index
                    element={
                      <ProtectedRoute userRole={userRole}>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute userRole={userRole}>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="clients" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'manager']} userRole={userRole}><Clients />
                  </ProtectedRoute>} />
                  <Route path="clients/add" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'manager']} userRole={userRole}><AddClient />
                  </ProtectedRoute>} />
                  <Route path="clients/edit/:id" element={<AddClient />} />
                  <Route path="projects" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'manager']} userRole={userRole}><Projects />
                  </ProtectedRoute>} />
                  <Route path="projects/add" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'manager']} userRole={userRole}><AddProject />
                  </ProtectedRoute>} />
                  <Route path="projects/edit/:id" element={<ProtectedRoute allowedRoles={['admin', 'hr', 'manager']} userRole={userRole}><AddProject />
                  </ProtectedRoute>} /> */}
                  {/* <Route path="finance" element={<FinanceTabs />} />
                  <Route path="invoices" element={<Invoices />} />
                  <Route path="add-invoice" element={<AddInvoice />} />
                  <Route path="add-payroll" element={<AddPayroll />} />
                  <Route path="timesheet" element={<TimesheetList />} />
                  <Route path="submit-timesheet" element={<Timesheet />} />
                  <Route path="manager-timesheet-approval" element={<ManagerTimesheetApproval />} />
                  <Route path="associates" element={<Associates />} />
                  <Route path="associates/add" element={<AddAssociate />} />
                  <Route path="associates/edit/:id" element={<AddAssociate />} />
                  <Route path="designations" element={<Designations />} />
                  <Route path="designations/add" element={<AddDesignation />} />
                  <Route path="designations/edit/:id" element={<AddDesignation />} /> */}
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <Users />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="profile" element={<Profile />} />
                  <Route
                    path="users/edit/:id"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <EditUser />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/onboard"
                    element={
                      <ProtectedRoute
                        allowedRoles={["admin"]}
                        userRole={userRole}
                      >
                        <OnboardUser />
                      </ProtectedRoute>
                    }
                  />
                  {/* <Route path="messages" element={<Messages />} /> */}
                </Route>
                <Route path="*" element={<NotFound />} />
              </Routes>
              <Toaster position="top-right" />
            </div>
          </AuthProvider>
        </Router>
      </ChakraProvider>
    </QueryClientProvider>
  );
}

export default App;
